import React, { useEffect } from "react";
import { createPortal } from "react-dom";

export interface PortaledModalFrameProps {
  children: React.ReactNode;
  onClose: () => void;
  /** Optional; defaults to modal-title */
  ariaLabelledBy?: string;
  /** Extra classes on the white panel (width caps, etc.) */
  panelClassName?: string;
  panelStyle?: React.CSSProperties;
}

/**
 * Full-screen dimmed backdrop (bg-black/60, same as Modal) via createPortal(document.body) with z-[100]
 * so dialogs sit above the app header. Panel is viewport-clamped (dvh + safe areas).
 * Put scrollable form content in a child with min-h-0 flex-1 overflow-y-auto.
 */
export function PortaledModalFrame({
  children,
  onClose,
  ariaLabelledBy = "modal-title",
  panelClassName = "",
  panelStyle,
}: PortaledModalFrameProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const node = (
    <div aria-labelledby={ariaLabelledBy} role="dialog" aria-modal="true">
      <div className="fixed inset-0 z-[100] flex min-h-0 items-center justify-center p-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:p-4">
        <button
          type="button"
          tabIndex={-1}
          className="absolute inset-0 cursor-default border-0 bg-black/60 p-0 transition-opacity"
          aria-hidden
          onClick={onClose}
        />
        <div
          className={`relative z-10 flex min-h-0 w-full max-w-[calc(100vw-1.5rem)] max-h-[min(90dvh,calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-1.5rem))] flex-col overflow-hidden rounded-lg bg-white text-left shadow-xl ${panelClassName}`}
          style={panelStyle}
        >
          {children}
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}
